import { useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import DOMPurify from 'dompurify';
import {
  CODE128_BARCODE_OPTS, LINE_HEIGHT, MARGIN_MM, QR_QUIET_MODULES, planCode128Label, planQrLabel,
} from './labelLayout';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export function LabelBarcode({ value, format, showValue = true }) {
  const svgRef = useRef(null);
  const [qr, setQr] = useState('');

  useEffect(() => {
    if (format === 'qr') {
      // margin は「モジュール数」指定。QR規格が要求する4モジュールの静粛領域を必ず描く —
      // これが無いとモジュールサイズが足りていてもスキャナは認識に失敗する。
      QRCode.toString(String(value || ''), { type: 'svg', margin: QR_QUIET_MODULES, errorCorrectionLevel: 'M' })
        .then(setQr).catch(() => setQr(''));
      return;
    }
    if (svgRef.current && value) {
      try {
        // 描画オプションは labelLayout.CODE128_BARCODE_OPTS が唯一の定義元
        // （静粛領域=細エレメント幅×10、アスペクト計算もそこから導出される）。
        JsBarcode(svgRef.current, String(value), { ...CODE128_BARCODE_OPTS, displayValue: showValue });
        // ensure namespace survives innerHTML serialization into the print window
        svgRef.current.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      } catch { /* invalid value for CODE128 */ }
    }
  }, [value, format, showValue]);

  if (format === 'qr') {
    return <div className="ql-qr" style={{ height: '100%' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(qr, { USE_PROFILES: { svg: true } }) }} />;
  }
  return <svg ref={svgRef} className="ql-bcsvg" />;
}

function TextLines({ plan, align }) {
  if (!plan.lines.length) return null;
  return (
    <div className="ql-lines" style={{ width: plan.textWidthMm ? `${plan.textWidthMm}mm` : '100%', textAlign: align }}>
      {plan.lines.map((l, i) => (
        <div key={`${l.kind}-${i}`} className="ql-line" style={{
          fontSize: `${l.sizeMm}mm`, lineHeight: LINE_HEIGHT, fontWeight: l.bold ? 700 : 400,
          marginTop: i ? `${plan.lineGapMm}mm` : 0,
        }}>{l.text}</div>
      ))}
    </div>
  );
}

// 設計値は labelLayout.js（縮小せず落とす・1ラベル1整列・識別子は必ず1行）。
function PlannedLabel({ data, w, h, plan }) {
  const wide = plan.layout === 'wide';
  const strip = plan.layout === 'strip';
  return (
    <div className="ql-label" style={{
      width: `${w}mm`, height: `${h}mm`, padding: `${plan.marginMm}mm`, color: '#000',
      flexDirection: wide ? 'row' : 'column',
      alignItems: strip ? 'flex-start' : 'center',
      justifyContent: wide ? 'flex-start' : 'center',
    }}>
      <div className={plan.format === 'qr' ? 'ql-qr' : 'ql-bc'} style={{
        height: `${plan.codeMm}mm`,
        ...(plan.format === 'qr'
          ? { width: `${plan.codeMm}mm`, flex: '0 0 auto' }
          // Code128 は必要な幅を取る（バーは幅がないと読めない）。枠のアスペクトを
          // SVG固有アスペクトに合わせているので等倍で収まり、文字も歪まない。
          : { width: `${plan.codeWidthMm}mm`, flex: '0 0 auto', justifyContent: 'flex-start' }),
        ...(wide
          ? { marginRight: `${plan.codeGapMm}mm` }
          : { marginBottom: plan.lines.length ? `${plan.codeGapMm}mm` : 0 }),
      }}>
        <LabelBarcode value={data.barcode} format={plan.format} showValue={plan.showCodeText !== false} />
      </div>
      <TextLines plan={plan} align={strip || wide ? 'left' : 'center'} />
    </div>
  );
}

/**
 * Renders one label at exact DK dimensions (w/h in mm; h=null → continuous, auto height).
 * Die-cut rolls go through the specified auto-layout (labelLayout.js) for both formats.
 * ロケーション と 長尺ロール は従来の高さ比例レイアウトのまま。
 */
export function LabelTemplate({ data, w, h, format }) {
  const plan = planQrLabel({ data, w, h, format }) || planCode128Label({ data, w, h, format });
  if (plan) return <PlannedLabel data={data} w={w} h={h} plan={plan} />;

  // 従来レイアウト（長尺ロールのみ）も共有の余白に揃える。
  const pad = MARGIN_MM;
  const usableH = h ? Math.max(h - pad * 2, 6) : null;
  const usableW = Math.max(w - pad * 2, 6);
  const gap = 1.0;                                   // mm gap between code and text
  const isLoc = data.type === 'location';
  const isQr = format === 'qr';
  const hasTitle = !!data.title;

  const fields = data.fields;
  const textLines = isLoc
    ? 1 + (data.subtitle ? 1 : 0)
    : (hasTitle ? 1 : 0) + (fields?.length || 0);

  let codeMm;
  if (isQr) {
    codeMm = clamp(Math.min(h ? usableH * ((hasTitle || fields?.length) ? 0.72 : 0.95) : 20, usableW), 15, 44);
  } else {
    codeMm = h ? clamp(usableH * (isLoc ? 0.30 : 0.36), 7, 14) : 13;
  }

  let fontMm = 3.0;
  if (h && textLines > 0) {
    const textH = Math.max(usableH - codeMm - gap, 3);
    const lineMm = textH / textLines;
    fontMm = clamp(lineMm / 1.28 * (isLoc ? 1.0 : 0.95), isLoc ? 2.2 : 1.7, isLoc ? 7 : 3.4);
  }

  return (
    <div className="ql-label" style={{ width: `${w}mm`, height: h ? `${h}mm` : 'auto', minHeight: h ? undefined : '16mm', padding: `${pad}mm` }}>
      <div className={isQr ? 'ql-qr' : 'ql-bc'} style={{ height: `${codeMm}mm`, marginBottom: textLines > 0 ? `${gap}mm` : 0 }}>
        <LabelBarcode value={data.barcode} format={format} />
      </div>
      {textLines > 0 && (
        <div className="ql-body" style={{ fontSize: `${fontMm}mm` }}>
          {isLoc ? (
            <>
              <div className="ql-big" style={{ fontSize: `${clamp(fontMm * 1.7, 3, 9)}mm` }}>{data.big}</div>
              {data.subtitle && <div className="ql-sub" style={{ fontSize: `${clamp(fontMm * 0.62, 1.6, 3)}mm` }}>{data.subtitle}</div>}
            </>
          ) : (
            <>
              {data.title && <div className="ql-ttl">{data.title}</div>}
              {fields?.length > 0 && (
                <div className="ql-fields">
                  {fields.map((f, i) => <div key={`${f.k}-${i}`}><b>{f.k}:</b> {f.v}</div>)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
